import { useEffect, useState } from "react";
import API from "@/lib/api";
import ReactMarkdown from "react-markdown";
import { ShieldAlert, Loader2, CheckCircle2, XCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export default function ForumConsentModal({ onDone, reconsent = false, existingDisplayName = "", existingAvatarMode = "initial" }) {
  const [step, setStep] = useState(1);
  const [agreed, setAgreed] = useState(false);
  const [displayName, setDisplayName] = useState(existingDisplayName || "");
  const [nameCheck, setNameCheck] = useState(existingDisplayName ? { status: "available", reason: "" } : { status: "idle", reason: "" });
  const [avatarMode, setAvatarMode] = useState(existingAvatarMode || "initial");
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    API.get("/forum/terms").then(({ data }) => setTerms(data)).catch(() => setTerms({ body_markdown: "", changelog: "" }));
  }, []);

  const checkName = async (name) => {
    if (name.trim().length < 2) {
      setNameCheck({ status: "invalid", reason: "Must be at least 2 characters" });
      return;
    }
    setNameCheck({ status: "checking", reason: "" });
    try {
      const { data } = await API.post("/forum/check-display-name", { display_name: name });
      setNameCheck({
        status: data.available ? "available" : "taken",
        reason: data.reason || (data.available ? "" : "That name is already taken"),
      });
    } catch {
      setNameCheck({ status: "idle", reason: "" });
    }
  };

  const submit = async () => {
    if (!agreed || nameCheck.status !== "available") return;
    setSubmitting(true);
    try {
      await API.post("/forum/consent", {
        display_name: displayName.trim(),
        avatar_mode: avatarMode,
        agreed_to_terms: true,
      });
      toast({ title: "Welcome to the ADHD Club Forum!" });
      onDone();
    } catch (err) {
      toast({
        title: "Couldn't complete setup",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="forum-consent-modal">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-card border border-border rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold leading-tight">
                {reconsent ? "Updated community terms" : "ADHD Club Forum"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {reconsent
                  ? "We've updated the forum terms. Please review and re-accept to continue posting."
                  : "Before you join, please read and accept the community terms."}
              </p>
            </div>
          </div>

          {reconsent && terms?.changelog && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900" data-testid="consent-reconsent-banner">
              <strong>What's new:</strong> {terms.changelog}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="consent-step-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>p]:mb-2 [&_strong]:font-semibold">
                {terms ? (
                  <ReactMarkdown>{terms.body_markdown || ""}</ReactMarkdown>
                ) : (
                  <div className="flex items-center gap-2 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Loading terms…</div>
                )}
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-agree-checkbox">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-primary"
                />
                <span className="text-sm">I have read and understand the above. I acknowledge that anything I share is at my own risk and own responsibility. I release Elevated Psychiatry from liability for user-posted content.</span>
              </label>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => window.history.back()}
                  data-testid="consent-decline-button"
                >
                  Decline &amp; leave
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  disabled={!agreed}
                  onClick={() => setStep(2)}
                  data-testid="consent-continue-button"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="consent-step-2">
              <div>
                <Label className="text-sm font-medium">Pick a display name (alias)</Label>
                <p className="text-xs text-muted-foreground mb-1.5">2–24 characters. This is what other members will see. Use an alias for privacy.</p>
                <Input
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    if (e.target.value.trim().length >= 2) checkName(e.target.value);
                    else setNameCheck({ status: "idle", reason: "" });
                  }}
                  maxLength={24}
                  placeholder="e.g. FocusFox, BrainWanderer"
                  className="h-11"
                  data-testid="consent-display-name-input"
                />
                <div className="text-xs mt-1.5 h-5 flex items-center gap-1">
                  {nameCheck.status === "checking" && (<><Loader2 className="w-3 h-3 animate-spin" /> checking…</>)}
                  {nameCheck.status === "available" && (<><CheckCircle2 className="w-3 h-3 text-green-600" /> <span className="text-green-700">available</span></>)}
                  {(nameCheck.status === "taken" || nameCheck.status === "invalid") && (
                    <><XCircle className="w-3 h-3 text-red-600" /> <span className="text-red-700">{nameCheck.reason}</span></>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">How should we show your avatar?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setAvatarMode("initial")}
                    className={`p-4 rounded-xl border-2 text-left transition-colors ${avatarMode === "initial" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
                    data-testid="consent-avatar-initial"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent font-semibold text-sm flex items-center justify-center border border-border">
                        {displayName.trim()[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold">Just initial</p>
                        <p className="text-xs text-muted-foreground">Most private</p>
                      </div>
                      {avatarMode === "initial" && <Check className="w-4 h-4 text-primary" />}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAvatarMode("picture")}
                    className={`p-4 rounded-xl border-2 text-left transition-colors ${avatarMode === "picture" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
                    data-testid="consent-avatar-picture"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/20 text-secondary flex items-center justify-center border border-border text-xs font-semibold">
                        IMG
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold">Profile picture</p>
                        <p className="text-xs text-muted-foreground">Uses your avatar</p>
                      </div>
                      {avatarMode === "picture" && <Check className="w-4 h-4 text-primary" />}
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  data-testid="consent-back-button"
                >
                  Back
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  onClick={submit}
                  disabled={submitting || nameCheck.status !== "available"}
                  data-testid="consent-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (reconsent ? "Accept updated terms" : "Join the forum")}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center pt-1">
                Forum data is handled under our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground" data-testid="consent-privacy-link">Privacy Policy</a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
